<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { sitesStatic } from '../composables/SiteList'
import { getAlerts, type Alert, type DeformationDetails, type ChangeDetectionDetails } from '../composables/alertsList'
import { RouterLink } from 'vue-router'
import type { DataTableRowClickEvent } from 'primevue/datatable'

const alerts = ref<Alert[]>(getAlerts())
const sites = ref(sitesStatic)
const loading = ref(true)
const selectedAlert = ref<Alert | null>(null)
const showAlertDialog = ref(false)
const showActionMessage = ref('')
const showActionMessageTimeout = ref<number | null>(null)

// Computed property to sort sites with alerts to the top
const sortedSites = computed(() => {
  return [...sites.value].sort((a, b) => {
    const aHasAlert = hasAlert(a.name)
    const bHasAlert = hasAlert(b.name)
    if (aHasAlert && !bHasAlert) return -1
    if (!aHasAlert && bHasAlert) return 1
    return a.name.localeCompare(b.name)
  })
})

// Simulate API call
onMounted(() => {
  setTimeout(() => {
    loading.value = false
  }, 1000)
})

const generateRandomDeformationDetails = (): DeformationDetails => {
  const features = ['coastal', 'infrastructure (ancillary)', 'infrastructure (core)', 'water'] as const
  const thresholds = [0.1, 0.5, 1] // 1mm, 5mm, 1cm in cm
  return {
    threshold: thresholds[Math.floor(Math.random() * thresholds.length)],
    measured: Math.round((Math.random() * 3 + 0.5) * 10) / 10,
    time_period: Math.floor(Math.random() * 365) + 30,
    accuracy: Math.round(Math.random() * 0.5 * 10) / 10,
    feature: features[Math.floor(Math.random() * features.length)],
    confidence: Math.floor(Math.random() * 40) + 40
  }
}

const generateRandomChangeDetails = (): ChangeDetectionDetails => {
  const allFeatures = ['coastal', 'infrastructure (ancillary)', 'infrastructure (core)', 'vegetation', 'water'] as const
  const nonInfraFeatures = ['coastal', 'vegetation', 'water'] as const
  
  // Randomly select prior feature
  const priorFeature = allFeatures[Math.floor(Math.random() * allFeatures.length)]
  
  // If prior feature is infrastructure, current feature must be either:
  // 1. The same infrastructure type, or
  // 2. A non-infrastructure type
  // If prior feature is not infrastructure, current feature must be non-infrastructure
  const currentFeature = priorFeature.includes('infrastructure')
    ? Math.random() < 0.5  // 50% chance to keep same infrastructure type
      ? priorFeature
      : nonInfraFeatures[Math.floor(Math.random() * nonInfraFeatures.length)]
    : nonInfraFeatures[Math.floor(Math.random() * nonInfraFeatures.length)]

  return {
    priorFeature,
    currentFeature,
    pValue: Math.random() * 0.01,
    confidence: Math.floor(Math.random() * 1000) + 1
  }
}

const addDummyAlert = () => {
  const alertTypes = ['deformation-rate', 'change-detection'] as const
  const randomType = alertTypes[Math.floor(Math.random() * alertTypes.length)]
  const randomSite = sites.value[Math.floor(Math.random() * sites.value.length)]
  
  alerts.value.push({
    id: String(alerts.value.length + 1),
    type: randomType,
    site: randomSite.name,
    location: randomSite.coords as [number, number],
    likelihood: Math.random(),
    severity: Math.random(),
    startDate: new Date().toISOString().split('T')[0],
    details: randomType === 'deformation-rate' 
      ? generateRandomDeformationDetails()
      : generateRandomChangeDetails()
  })
}

const hasAlert = (siteName: string) => {
  return alerts.value.some(alert => alert.site === siteName)
}

const formatDeformationDetails = (details: DeformationDetails) => {
  return {
    threshold: `${details.threshold} cm`,
    measured: `${details.measured} cm`,
    time_period: `${details.time_period} days`,
    accuracy: `±${details.accuracy} cm`,
    feature: details.feature,
    confidence: `${details.confidence}%`
  }
}

const formatChangeDetails = (details: ChangeDetectionDetails) => {
  return {
    priorFeature: details.priorFeature,
    currentFeature: details.currentFeature,
    pValue: details.pValue.toExponential(3),
    confidence: `${details.confidence}:1`
  }
}

const openAlertDialog = (event: DataTableRowClickEvent) => {
  selectedAlert.value = event.data as Alert
  showAlertDialog.value = true
}

const dismissAlert = () => {
  if (selectedAlert.value) {
    alerts.value = alerts.value.filter(a => a.id !== selectedAlert.value!.id)
    showAlertDialog.value = false
    showActionMessage.value = `Alert ${selectedAlert.value.id} has been dismissed`
    clearActionMessageTimeout()
    showActionMessageTimeout.value = window.setTimeout(() => {
      showActionMessage.value = ''
    }, 3000)
  }
}

const flagAlert = () => {
  if (selectedAlert.value) {
    showActionMessage.value = `Alert ${selectedAlert.value.id} has been flagged`
    clearActionMessageTimeout()
    showActionMessageTimeout.value = window.setTimeout(() => {
      showActionMessage.value = ''
    }, 3000)
  }
}

const clearActionMessageTimeout = () => {
  if (showActionMessageTimeout.value) {
    clearTimeout(showActionMessageTimeout.value)
    showActionMessageTimeout.value = null
  }
}

const goToSite = () => {
  if (selectedAlert.value) {
    window.location.href = `/site/${selectedAlert.value.site}`
  }
}
</script>

<template>
  <div class="alerts-container">
    <div class="alerts-layout">
      <div class="left-column">
        <h1>Site Status</h1>
        <div v-if="loading" class="loading-container">
          <div class="loading-content">
            <ProgressSpinner />
            <p>Loading sites...</p>
          </div>
        </div>
        <DataTable v-else :value="sortedSites" class="p-datatable-sm">
          <Column field="name" header="Site Name">
            <template #body="{ data }">
              <div class="site-row">
                <i v-if="hasAlert(data.name)" class="pi pi-exclamation-triangle text-red-500"></i>
                <span :class="{ 'text-red-500': hasAlert(data.name) }">{{ data.name }}</span>
              </div>
            </template>
          </Column>
        </DataTable>
      </div>
      
      <div class="right-column">
        <div class="alerts-header">
          <h1>Alerts List</h1>
        </div>
        
        <div v-if="alerts.length === 0" class="no-alerts">
          <div class="no-alerts-content">
            <i class="pi pi-info-circle"></i>
            <p>No alerts at this time</p>
          </div>
        </div>
        
        <DataTable 
          v-else 
          :value="alerts" 
          class="p-datatable-sm alerts-table"
          @row-click="openAlertDialog"
          selectionMode="single"
        >
          <Column field="id" header="ID" />
          <Column field="type" header="Type" />
          <Column field="site" header="Site" />
          <Column field="location" header="Location" />
          <Column field="startDate" header="Time" />
          <Column field="details" header="Details">
            <template #body="{ data }">
              <div v-if="data.type === 'deformation-rate'" class="details-grid">
                <div class="detail-label">Threshold:</div>
                <div>{{ formatDeformationDetails(data.details as DeformationDetails).threshold }}</div>
                <div class="detail-label">Measured:</div>
                <div>{{ formatDeformationDetails(data.details as DeformationDetails).measured }}</div>
                <div class="detail-label">Time Period:</div>
                <div>{{ formatDeformationDetails(data.details as DeformationDetails).time_period }}</div>
                <div class="detail-label">Accuracy:</div>
                <div>{{ formatDeformationDetails(data.details as DeformationDetails).accuracy }}</div>
                <div class="detail-label">Feature:</div>
                <div>{{ formatDeformationDetails(data.details as DeformationDetails).feature }}</div>
                <div class="detail-label">Confidence:</div>
                <div>{{ formatDeformationDetails(data.details as DeformationDetails).confidence }}</div>
              </div>
              <div v-else class="details-grid">
                <div class="detail-label">Prior Feature:</div>
                <div>{{ formatChangeDetails(data.details as ChangeDetectionDetails).priorFeature }}</div>
                <div class="detail-label">Current Feature:</div>
                <div>{{ formatChangeDetails(data.details as ChangeDetectionDetails).currentFeature }}</div>
                <div class="detail-label">P Value:</div>
                <div>{{ formatChangeDetails(data.details as ChangeDetectionDetails).pValue }}</div>
                <div class="detail-label">Confidence:</div>
                <div>{{ formatChangeDetails(data.details as ChangeDetectionDetails).confidence }}</div>
              </div>
            </template>
          </Column>
        </DataTable>

        <div class="add-alert-button">
          <Button label="Add Dummy Alert" @click="addDummyAlert" />
        </div>
      </div>
    </div>
  </div>

  <Dialog v-model:visible="showAlertDialog" modal header="Alert Inspector" :style="{ width: '50vw' }">
    <div v-if="selectedAlert">
      <div class="dialog-section">
        <h2 class="dialog-title">Alert Details</h2>
        <div class="dialog-grid">
          <div class="dialog-label">ID:</div>
          <div>{{ selectedAlert.id }}</div>
          <div class="dialog-label">Type:</div>
          <div>{{ selectedAlert.type }}</div>
          <div class="dialog-label">Site:</div>
          <div>{{ selectedAlert.site }}</div>
          <div class="dialog-label">Date:</div>
          <div>{{ selectedAlert.startDate }}</div>
        </div>
      </div>

      <div class="dialog-section">
        <h3 class="dialog-subtitle">Measurements</h3>
        <div v-if="selectedAlert.type === 'deformation-rate'" class="dialog-grid">
          <div class="dialog-label">Threshold:</div>
          <div>{{ formatDeformationDetails(selectedAlert.details as DeformationDetails).threshold }}</div>
          <div class="dialog-label">Measured:</div>
          <div>{{ formatDeformationDetails(selectedAlert.details as DeformationDetails).measured }}</div>
          <div class="dialog-label">Time Period:</div>
          <div>{{ formatDeformationDetails(selectedAlert.details as DeformationDetails).time_period }}</div>
          <div class="dialog-label">Accuracy:</div>
          <div>{{ formatDeformationDetails(selectedAlert.details as DeformationDetails).accuracy }}</div>
          <div class="dialog-label">Feature:</div>
          <div>{{ formatDeformationDetails(selectedAlert.details as DeformationDetails).feature }}</div>
          <div class="dialog-label">Confidence:</div>
          <div>{{ formatDeformationDetails(selectedAlert.details as DeformationDetails).confidence }}</div>
        </div>
        <div v-else class="dialog-grid">
          <div class="dialog-label">Prior Feature:</div>
          <div>{{ formatChangeDetails(selectedAlert.details as ChangeDetectionDetails).priorFeature }}</div>
          <div class="dialog-label">Current Feature:</div>
          <div>{{ formatChangeDetails(selectedAlert.details as ChangeDetectionDetails).currentFeature }}</div>
          <div class="dialog-label">P Value:</div>
          <div>{{ formatChangeDetails(selectedAlert.details as ChangeDetectionDetails).pValue }}</div>
          <div class="dialog-label">Confidence:</div>
          <div>{{ formatChangeDetails(selectedAlert.details as ChangeDetectionDetails).confidence }}</div>
        </div>
      </div>

      <div class="dialog-buttons">
        <Button label="Go to Site" icon="pi pi-map-marker" @click="goToSite" />
        <Button label="Flag Alert" icon="pi pi-flag" severity="danger" @click="flagAlert" />
        <Button label="Dismiss Alert" icon="pi pi-times" severity="info" @click="dismissAlert" />
      </div>
    </div>
  </Dialog>

  <Toast position="top-right" />
  <div v-if="showActionMessage" class="fixed top-4 right-4 bg-white shadow-lg rounded-lg p-4 z-50">
    {{ showActionMessage }}
  </div>

  <Divider />
</template>

<style scoped>
/* Main container */
.alerts-container {
  padding: 1rem;
  width: 100%;
}

/* Two-column layout */
.alerts-layout {
  display: flex;
  gap: 2rem;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

/* Left column - Site Status (1/3 width) */
.left-column {
  flex: 1;
  min-width: 200px;
  max-width: 33.33%;
}

/* Right column - Alerts List (2/3 width) */
.right-column {
  flex: 2;
  min-width: 400px;
  overflow: hidden;
}

:deep(.p-datatable) {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: auto;
  max-width: 100%;
}

:deep(.p-datatable .p-datatable-table) {
  min-width: 100%;
  width: auto;
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
  background: #f3f4f6;
  padding: 0.75rem;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
  padding: 0.75rem;
}

:deep(.p-datatable .p-datatable-tbody > tr:hover) {
  background: #f9fafb;
  cursor: pointer;
}

/* Red text for alerts */
.text-red-500 {
  color: #ef4444;
}

/* Dialog content styling */
:deep(.p-dialog-content) {
  padding: 1rem;
}

.dialog-section {
  margin-bottom: 1.5rem;
}

.dialog-title {
  font-size: 1.25rem;
  font-weight: bold;
  margin-bottom: 1rem;
}

.dialog-subtitle {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.dialog-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.dialog-label {
  font-weight: 600;
}

.dialog-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
}
</style>
